<template>
  <div :id="release.EncryptedId" :ref="release.EncryptedId" class="item-wrapper">
    <a :href="getRoutePath('NewsProfile', release.EncryptedId)" v-on:click.prevent="viewSingleNews(release.EncryptedId)">
      <div class="item-inner">
        <div class="image spacing-sm"
             v-bind:style="{ 'background-image': 'url(' + getImages + ')' }">
        </div>

        <div class="type-wrapper spacing-sm">
          <div class="type" :class="[`${release.InformationType}-color`]">
            <p>{{ releaseTypeHandler(release.InformationType) }}</p>
          </div>

          <div class="is-regulatory">
            <p v-if="release.IsRegulatory">Regulatory</p>
            <p v-else>Non-regulatory</p>
          </div>
        </div>

        <div class="title spacing-xs">
          <h4>{{ release.Title }}</h4>
        </div>

        <div class="date spacing-sm">
          <p>{{ date }} {{ getTimeZone }} ({{ fromNowDate }})</p>
        </div>

        <div class="description spacing-sm">
          <p>{{ release.Intro }}</p>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';
  import {mixinRoutesHandler} from '@/mixins/routes-handler';

  // Configs
  import DatesConfig from "@/configs/dates";

  // Constant
  const UI_DEFAULT_DATE_TIME_FORMAT = DatesConfig.UI_DEFAULT_DATE_TIME_FORMAT;

  export default {
    name: 'ReleaseItemBig',

    props: {
      release: {},
      targetElm: String
    },

    mixins: [mixinDatesHandler, mixinRoutesHandler],

    data: function() {
      return {
        placeholder: require('@/assets/images/news/placeholder.png'),
      }
    },

    methods: {
      getRoutePath(routeName, releaseId) {
        return this._mixinGetRoutePathWithParams(routeName, {releaseId: releaseId});
      },

      viewSingleNews(releaseId) {
        // For history set clicked item query
        const pageIndex   = this.pageIndexUrlParam;
        const searchQuery = this.searchQueryUrlParam;

        let query = {
          index: pageIndex,
          query: searchQuery,
          release: releaseId,
        }

        if (pageIndex <= 0) {
          delete query.index;
        }

        if (!searchQuery) {
          delete query.query;
        }

        // Used for history
        this.$router.push({
          query: query
        }).catch(()=>{});

        // Go to single view
        this.$router.push({
          name: 'NewsProfile',
          params: {releaseId: releaseId},
        }).catch(()=>{});
      },

      scrollToTargetElm() {
        if (this.release.EncryptedId === this.targetElm) {
          document.getElementById(this.targetElm).scrollIntoView();
        }
      },

      releaseTypeHandler(type) {
        if (type === 'KMK') {
          return 'Financial';
        } else if (type === 'RDV') {
          return 'Reports';
        } else if (type === 'PRM') {
          return 'Press';
        } else if (type === 'RPT') {
          return 'Reports';
        } else if (type === 'INB') {
          return 'Invitations';
        } else if (type === 'NBR') {
          return 'News';
        }

        return 'Other';
      }
    },

    computed: {
      getTimeZone() {
        return this._mixinDateHandler_GetTimeZoneAbbr();
      },

      getImages() {
        if (this.release.Images.length === 0 || !('UrlTo800x800ArResized' in this.release.Images[0])) {
          return this.placeholder; // placeholder image
        }

        return this.release.Images[0].UrlTo800x800ArResized;
      },

      fromNowDate() {
        const date = this.release.PublishDate;

        if (!this._mixinDateHandler_IsValidDate(date)) {
          return '';
        }

        return this._mixinDateHandler_ApiResponseDateFromNow(date, true);
      },

      date() {
        const date = this.release.PublishDate;

        if (!this._mixinDateHandler_IsValidDate(date)) {
          return '';
        }

        return this._mixinDateHandler_ApiResponseDateTimeToFormat(date, UI_DEFAULT_DATE_TIME_FORMAT, true);
      },

      pageIndexUrlParam() {
        let index = this.$route.query.index;

        if (!parseInt(index)) {
          return 1;
        }

        return parseInt(index);
      },

      searchQueryUrlParam() {
        let searchQuery = this.$route.query.query;

        if (!searchQuery) {
          return null;
        }

        return searchQuery;
      },
    },

    mounted() {
      this.$nextTick(function () {
        let _this = this;
        setTimeout(function(){
          _this.scrollToTargetElm();
        }, 100);
      });
    }
  }
</script>

<style scoped lang="scss">
.item-wrapper {
  width: 100%;
  margin-bottom: 60px;

  &:hover {
    .title {
      h4 {
        text-decoration: underline;
      }
    }
  }

  a {
    display: block;
    text-decoration: none;
  }

  .item-inner {
    width: 100%;

    .image {
      width: 100%;
      height: 350px;
      background-size: cover;
      background-position: 50% 50%;
    }

    .type-wrapper {
      width: 100%;

      .type {
        display: inline-block;
        padding: 3px 15px;
        border-radius: 20px;
        color: white;
        margin-right: 10px;

        p {
          font-size: 14px;
          line-height: 1.6;
          font-weight: bold;
          margin-bottom: 0;
        }

        &.KMK-color {
          background-color: #192e5b;
        }

        &.RDV-color {
          background-color: #1e65a7;
        }

        &.PRM-color {
          background-color: #73a2c0;
        }

        &.RPT-color {
          background-color: #00743f;
        }

        &.INB-color {
          background-color: #f1a104;
        }

        &.NBR-color {
          background-color: #9a1c1c;
        }
      }

      .is-regulatory {
        display: inline-block;
        padding: 3px 15px;
        border-radius: 20px;
        color: white;

        p {
          font-size: 14px;
          line-height: 1.6;
          font-weight: bold;
          margin-bottom: 0;
        }

        background-color: #959595;
      }
    }

    .title {
      width: 100%;

      h4 {
        margin-bottom: 0;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }

    .date {
      width: 100%;

      p {
        font-size: 12px;
        color: #757575;
        margin-bottom: 0;
      }
    }

    .description {
      width: 100%;

      p {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
      }
    }

    .spacing-xs {
      margin-bottom: 5px;
    }

    .spacing-sm {
      margin-bottom: 10px;
    }
  }
}
</style>
